import React, {useEffect, useState} from 'react';
import {classes, style} from './doppe-payment-element.st.css';
import {
    AddressElement,
    ButtonType,
    PaymentElement,
    reactCreateMemo,
    StripePaymentElement,
    StripePaymentElementLocales,
    TextBoxTypes,
    useAsyncOperation,
    useFormController
} from '@wix/devzai-utils-react';
import {
    DoppeActionTypeWidgetInnerButton,
} from '../doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import type {Price} from '@wix/devzai-utils-common';
import {
    arrayFirst,
    assertDefined,
    currencyFormatPrice,
    enumValueOrDefault,
    evaluateFunction,
    validateIsEmail,
    validateIsMobilePhone, AsyncValueStates } from '@wix/devzai-utils-common';
import {DoppeActionWidgetTextBox} from '../doppe-action-widget-text-box/doppe-action-widget-text-box';
import {FlexLayout, WixLoader, WixLoaderSize} from '@wix/devzai-common-client';
import {useBymoPageProps} from '../../client-server-common';
import {DoppeViewerFormFieldsGroup} from '../doppe-viewer-form-fields-group/doppe-viewer-form-fields-group';
import {DoppeViewerFormField} from '../doppe-viewer-form-field/doppe-viewer-form-field';
import {DoppeViewerForm} from '../doppe-viewer-form/doppe-viewer-form';
import {DoppePaymentElementDummy} from './doppe-payment-element-dummy';
import {DummyAddressElement} from './doppe-address-element-dummy/doppe-payment-element-dummy';
import {
    DoppeEmailSubscriptionConsentElement,
} from '../doppe-email-subscription-consent-element/doppe-email-subscription-consent-element';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {
    bymoPageStyleResolveInputPlaceholderColor,
    bymoPageStyleResolveInputTextColor,
    BymoPageStyleScopeColors,
    useBymoPageScopeColor,
} from '../bymo-page-style/bymo-page-style';
import {useDoppeViewerLang} from '../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import type {DoppeDtoCheckoutIntent} from '../../client-server-common/types/doppe-dto-checkout-intent';
import {useDoppeViewerClientServices} from "../../client/doppe-user-app/doppe-viewer-client-services-react";

export const DoppePaymentElement = reactCreateMemo(function DoppePaymentElement(props: DoppePaymentElement.Props) {
    const {
        returnUrl,
        onError,
        onDone,
        onSubmitPaymentClick,
        showAddressInput,
        showPhoneInput,
        price,
        createStripePaymentIntent,
        saveCheckoutIntent,
        demoMode,
        showConsent = false,
        consentText,
        showConsentCheckbox = false,
        previewCheckoutButtonText,
    } = props;

    const clientServices = useDoppeViewerClientServices();

    const doppeViewerLocales = useDoppeViewerLocales();

    const lang = useDoppeViewerLang();

    const [formSubmissionOperation, setFormSubmissionOperation] = useAsyncOperation();

    const [paymentElementLoading, setPaymentElementLoading] = useState(true);
    const [addressElementLoading, setAddressElementLoading] = useState(true);
    const isPaymentElementsLoading = paymentElementLoading || (addressElementLoading && showAddressInput);

    const [paymentIntent, setPaymentIntent] = useState<
        Promise<{ clientSecret: string; paymentIntentId: string }> | undefined
    >();
    useEffect(() => {
        setPaymentIntent(createStripePaymentIntent());
    }, []);

    type FormData = {
        email: string;
        name: string;
        phone: string;
        address: string;
        consent: boolean;
    };

    const formController = useFormController<FormData>(
        {
            email: '',
            phone: '',
            name: '',
            address: '',
            consent: false,
        },
        {
            validationFunction: (_formData, errorsReporter) => {
                if (demoMode) {
                    return;
                }

                errorsReporter.report('phone', (phone) => {
                    if (showPhoneInput) {
                        if (phone === '') {
                            return doppeViewerLocales['requiredField'];
                        } else if (!validateIsMobilePhone(phone)) {
                            return doppeViewerLocales['enterValidPhone'];
                        }
                    }
                    return undefined;
                });

                errorsReporter.report('email', (email) => {
                    if (email === '') {
                        return doppeViewerLocales['requiredField'];
                    } else if (!validateIsEmail(email)) {
                        return doppeViewerLocales['enterValidEmail'];
                    }
                    return undefined;
                });

                errorsReporter.report('name', (name) => {
                    if (name === '') {
                        return doppeViewerLocales['requiredField'];
                    }
                    return undefined;
                });
            },
        },
    );

    const phoneInputError = formController.formFieldsErrors.phone;
    const emailInputError = formController.formFieldsErrors.email;
    const nameInputError = formController.formFieldsErrors.name;

    const bymoPageProps = useBymoPageProps();
    const font = bymoPageProps.textFontFamily + ', sans-serif';

    const inputBackgroundColor = useBymoPageScopeColor(BymoPageStyleScopeColors.InputFieldBackgroundColor);
    const errorColor = useBymoPageScopeColor(BymoPageStyleScopeColors.ErrorTextColor);
    const successColor = useBymoPageScopeColor(BymoPageStyleScopeColors.SuccessTextColor);
    const widgetTextColor = useBymoPageScopeColor(BymoPageStyleScopeColors.TextColor);
    const inputTextColor = bymoPageStyleResolveInputTextColor(inputBackgroundColor);
    const colorTextPlaceholder = bymoPageStyleResolveInputPlaceholderColor(inputBackgroundColor);

    const fonts = [
        {
            cssSrc: `https://static.parastorage.com/tag-bundler/api/v1/fonts-cache/googleAPI/css?family=${bymoPageProps.textFontFamily}:400,600&display=swap`,
        },
    ];

    const [errorMessage, setErrorMessage] = useState<string>();

    const formattedAmount = currencyFormatPrice({
        price: price.amount,
        code: price.currencyCode,
        locale: lang,
    });

    return (
        <StripePaymentElement
            stripe={!demoMode ? clientServices.stripeService.getStripe() : null}
            demoMode={demoMode}
            price={price}
            returnUrl={returnUrl}
            fonts={fonts}
            locale={enumValueOrDefault(StripePaymentElementLocales, bymoPageProps.lang, StripePaymentElementLocales.Auto)}
            appearance={{
                variables: {
                    fontFamily: font,
                    fontSizeSm: '14px',
                    borderRadius: '4px',
                    colorBackground: inputBackgroundColor,
                    colorText: inputTextColor,
                    colorDanger: errorColor,
                    colorTextPlaceholder: colorTextPlaceholder,
                    colorSuccess: successColor,
                },
                rules: {
                    '.Input': {
                        boxShadow: 'none',
                        border: 'none',
                    },
                    '.Input:focus': {
                        boxShadow: 'none',
                        border: 'none',
                    },
                    '.Label': {
                        lineHeight: '1.5',
                        fontFamily: font,
                        color: widgetTextColor,
                    },
                },
            }}
        >
            {({ confirmPayment }) => (
                <DoppeViewerForm
                    className={style(classes.form)}
                    noValidate={true}
                    onSubmit={formController.createSubmitEventHandler(
                        async (formData) => {
                            try {
                                await setFormSubmissionOperation(
                                    evaluateFunction(async () => {
                                        setErrorMessage('');
                                        onSubmitPaymentClick();
                                        const {
                                            name,
                                            email,
                                            phone,
                                            address,
                                            consent,
                                        } = formData;

                                        const {
                                            clientSecret,
                                            paymentIntentId,
                                        } = assertDefined(await paymentIntent);
                                        await saveCheckoutIntent({
                                            uouCheckoutData: {
                                                uouName: name,
                                                uouEmail: email,
                                                uouAddress: address,
                                                uouPhone: phone,
                                                uouEmailSubscription: consent,
                                            },
                                            paymentIntentId: paymentIntentId,
                                        });
                                        const {
                                            error,
                                            status,
                                        } = await confirmPayment({
                                            name: name,
                                            email: email,
                                            phone: phone,
                                            stripePaymentIntentClientSecret: clientSecret,
                                        });
                                        if (error) {
                                            setErrorMessage(error.message);
                                        } else {
                                            onDone?.({
                                                name: name,
                                                email: email,
                                                status: status,
                                                consent: consent,
                                                paymentIntentId: paymentIntentId,
                                            });
                                        }
                                    }),
                                );
                            } catch (e: any) {
                                console.error(e);
                                onError({
                                    type: 'unknown',
                                    message: doppeViewerLocales['paymentError'],
                                });
                                setErrorMessage(e.message);
                            }
                        },
                        {
                            validationFailureCallback: (formFieldsErrors) => {
                                const invalidFields = Object.entries(formFieldsErrors)
                                    .filter(([_, error]) => error !== undefined)
                                    .map(([fieldName]) => fieldName);
                                onSubmitPaymentClick(invalidFields);
                            },
                        },
                    )}
                >
                    <DoppeViewerFormFieldsGroup title={doppeViewerLocales['checkoutContactInformation']}>
                        {showAddressInput ? null : (
                            <DoppeViewerFormField
                                label={doppeViewerLocales['fullName']}
                                inputError={nameInputError ? arrayFirst(nameInputError) : undefined}
                            >
                                <DoppeActionWidgetTextBox
                                    className={style(classes.textBox, {})}
                                    hasError={nameInputError !== undefined}
                                    shouldValidateValue={false}
                                    placeholder={doppeViewerLocales['contactFormNameFieldPlaceholder']}
                                    value={formController.getValue('name')}
                                    onTextChange={(value) => {
                                        formController.setValue('name', value);
                                    }}
                                />
                            </DoppeViewerFormField>
                        )}
                        <DoppeViewerFormField
                            label={doppeViewerLocales['email']}
                            inputError={emailInputError ? arrayFirst(emailInputError) : undefined}
                        >
                            <DoppeActionWidgetTextBox
                                className={style(classes.textBox, {})}
                                hasError={emailInputError !== undefined}
                                type={TextBoxTypes.Email}
                                shouldValidateValue={false}
                                placeholder={doppeViewerLocales['contactFormEmailFieldPlaceholder']}
                                value={formController.getValue('email')}
                                onTextChange={(value) => {
                                    formController.setValue('email', value);
                                }}
                            />
                        </DoppeViewerFormField>
                        {showPhoneInput && !showAddressInput ? (
                            <DoppeViewerFormField
                                label={doppeViewerLocales['checkoutPhone']}
                                inputError={phoneInputError ? arrayFirst(phoneInputError) : undefined}
                            >
                                <DoppeActionWidgetTextBox
                                    className={style(classes.textBox, {})}
                                    hasError={phoneInputError !== undefined}
                                    type={TextBoxTypes.PhoneNumber}
                                    shouldValidateValue={false}
                                    placeholder={doppeViewerLocales['contactFormPhoneFieldPlaceholder']}
                                    value={formController.getValue('phone')}
                                    onTextChange={(value) => {
                                        formController.setValue('phone', value);
                                    }}
                                />
                            </DoppeViewerFormField>
                        ) : null}
                    </DoppeViewerFormFieldsGroup>
                    {isPaymentElementsLoading ? (
                        <div className={style(classes.loader, FlexLayout.centerContent)}>
                            <WixLoader size={WixLoaderSize.Medium} />
                        </div>
                    ) : null}
                    <div
                        className={style(
                            classes.paymentElementsContainer,
                            { isLoading: isPaymentElementsLoading },
                            FlexLayout.column.default,
                        )}
                    >
                        <DoppeViewerFormFieldsGroup title={doppeViewerLocales['paymentDetails']}>
                            {demoMode ? (
                                <DoppePaymentElementDummy
                                    onLoaderStart={() => {
                                        setPaymentElementLoading(false);
                                    }}
                                />
                            ) : (
                                <PaymentElement
                                    onLoaderStart={() => {
                                        setPaymentElementLoading(false);
                                    }}
                                    onError={onError}
                                />
                            )}
                        </DoppeViewerFormFieldsGroup>
                        {showAddressInput ? (
                            <DoppeViewerFormFieldsGroup title={doppeViewerLocales['billingDetails']}>
                                {demoMode ? (
                                    <DummyAddressElement
                                        collectPhone={showPhoneInput}
                                        onLoaderStart={() => {
                                            setAddressElementLoading(false);
                                        }}
                                    />
                                ) : (
                                    <AddressElement
                                        onChange={(params) => {
                                            const {
                                                address,
                                                phone,
                                                name,
                                            } = params;
                                            const addressString = [address.line1, address.line2, address.city, address.state, address.country, address.postal_code]
                                                .map(part => (part || '').trim())
                                                .filter(part => part !== '')
                                                .join(', ');
                                            formController.setFormData({
                                                email: formController.formData.email,
                                                phone: showPhoneInput ? phone ?? '' : formController.formData.phone,
                                                name: name,
                                                address: addressString,
                                                consent: formController.formData.consent,
                                            });
                                        }}
                                        collectPhone={showPhoneInput}
                                        onLoaderStart={() => {
                                            setAddressElementLoading(false);
                                        }}
                                        onError={onError}
                                    />
                                )}
                            </DoppeViewerFormFieldsGroup>
                        ) : null}
                    </div>
                    {showConsent ? (
                        <div className={style(classes.emailSubscriptionConsent, {})}>
                            <DoppeEmailSubscriptionConsentElement
                                customText={consentText ?? ''}
                                showCheckbox={showConsentCheckbox}
                                onChange={(value) => {
                                    formController.setValue('consent', value);
                                }}
                                value={formController.getValue('consent')}
                            />
                        </div>
                    ) : null}
                    {errorMessage ? <div className={style(classes.errorMessage, {})}>{errorMessage}</div> : null}
                    <DoppeActionTypeWidgetInnerButton
                        className={style(classes.payButton, {})}
                        buttonType={ButtonType.SubmitButton}
                        isLoading={formSubmissionOperation?.state === AsyncValueStates.Pending}
                    >
                        {previewCheckoutButtonText ?? doppeViewerLocales['checkoutButton']({ amount: formattedAmount })}
                    </DoppeActionTypeWidgetInnerButton>
                    <div className={style(classes.securedPaymentByStripe, {})}>
                        {doppeViewerLocales['securedPaymentByStripe']}
                    </div>
                </DoppeViewerForm>
            )}
        </StripePaymentElement>
    );
});

export namespace DoppePaymentElement {
    export type DoneCallbackArgs = {
        name: string;
        email: string;
        consent: boolean;
        message?: string;
        status: StripePaymentElement.PaymentStatus;
        paymentIntentId: string;
    };

    export type ErrorCallbackArgs = {
        type: string;
        message?: string;
    };

    export interface Props {
        returnUrl: string;
        onError: (eventArgs: ErrorCallbackArgs) => void;
        onDone: (eventArgs: DoneCallbackArgs) => void;
        onSubmitPaymentClick: (invalidFields?: string[]) => void;
        showAddressInput?: boolean;
        showPhoneInput?: boolean;
        showEmailInput?: boolean;
        price: Price;
        createStripePaymentIntent: () => Promise<{ clientSecret: string; paymentIntentId: string }>;
        saveCheckoutIntent: (options: {
            uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData;
            paymentIntentId: string;
        }) => Promise<void>;
        demoMode?: boolean;
        showConsent?: boolean;
        consentText?: string;
        showConsentCheckbox?: boolean;

        previewCheckoutButtonText?: string;
    }
}
